import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SignaturePad from 'signature_pad';

const API_URL = '/api';

// 统计卡片组件
const StatCard = ({ title, value, subtitle, color = 'blue', icon, trend }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200'
  };

  return (
    <div className={`p-6 rounded-lg border-2 ${colorClasses[color]} transition-all duration-200 hover:shadow-lg`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium opacity-75">{title}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
          {subtitle && <p className="text-sm mt-1 opacity-60">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center mt-2 text-sm ${trend.type === 'up' ? 'text-green-600' : trend.type === 'down' ? 'text-red-600' : 'text-gray-600'}`}>
              {trend.type === 'up' && <span>↗️</span>}
              {trend.type === 'down' && <span>↘️</span>}
              {trend.type === 'equal' && <span>➡️</span>}
              <span className="ml-1">{trend.text}</span>
            </div>
          )}
        </div>
        {icon && <div className="text-4xl opacity-30 ml-4">{icon}</div>}
      </div>
    </div>
  );
};

// 时间范围选择器组件（合并高级筛选）
const TimeRangeSelector = ({ onRangeChange, currentRange, onFilterChange, currentFilter }) => {
  const [startYear, setStartYear] = useState(currentRange?.startYear || new Date().getFullYear());
  const [startMonth, setStartMonth] = useState(currentRange?.startMonth || 1);
  const [endYear, setEndYear] = useState(currentRange?.endYear || new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(currentRange?.endMonth || 6);
  
  // 筛选状态
  const [filters, setFilters] = useState({
    role: '',
    signedStatus: '',
    amountRange: '',
    ...currentFilter
  });

  const presetRanges = [
    { label: '本月', getValue: () => {
      const now = new Date();
      return { startYear: now.getFullYear(), startMonth: now.getMonth() + 1, endYear: now.getFullYear(), endMonth: now.getMonth() + 1 };
    }},
    { label: '本季度', getValue: () => {
      const now = new Date();
      const quarter = Math.floor(now.getMonth() / 3);
      return { startYear: now.getFullYear(), startMonth: quarter * 3 + 1, endYear: now.getFullYear(), endMonth: quarter * 3 + 3 };
    }},
    { label: '本年度', getValue: () => {
      const year = new Date().getFullYear();
      return { startYear: year, startMonth: 1, endYear: year, endMonth: 12 };
    }},
    { label: '上月', getValue: () => {
      const now = new Date();
      const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
      const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      return { startYear: year, startMonth: lastMonth, endYear: year, endMonth: lastMonth };
    }}
  ];

  const handlePresetClick = (preset) => {
    const range = preset.getValue();
    setStartYear(range.startYear);
    setStartMonth(range.startMonth);
    setEndYear(range.endYear);
    setEndMonth(range.endMonth);
    onRangeChange(range);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleCustomQuery = () => {
    const range = { startYear, startMonth, endYear, endMonth };
    onRangeChange(range);
    if (onFilterChange) {
      onFilterChange(filters);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
      <h3 className="text-lg font-medium mb-4 flex items-center">
        <span className="mr-2">📅</span>
        统计时间范围与筛选条件
      </h3>
      
      {/* 快速选择按钮 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {presetRanges.map((preset, index) => (
          <button
            key={index}
            onClick={() => handlePresetClick(preset)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* 自定义时间选择 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 时间范围 */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">时间范围</h4>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">起始：</label>
              <select
                value={startYear}
                onChange={(e) => setStartYear(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => (
                  <option key={2024 + i} value={2024 + i}>{2024 + i}年</option>
                ))}
              </select>
              <select
                value={startMonth}
                onChange={(e) => setStartMonth(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}月</option>
                ))}
              </select>
            </div>

            <span className="text-gray-500">至</span>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">结束：</label>
              <select
                value={endYear}
                onChange={(e) => setEndYear(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => (
                  <option key={2024 + i} value={2024 + i}>{2024 + i}年</option>
                ))}
              </select>
              <select
                value={endMonth}
                onChange={(e) => setEndMonth(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}月</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 筛选条件 */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">筛选条件</h4>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">角色筛选</label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">全部角色</option>
                <option value="admin">管理员</option>
                <option value="user">用户（待分配）</option>
                <option value="teacher">教师</option>
                <option value="caregiver">后勤</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">签名状态</label>
              <select
                value={filters.signedStatus}
                onChange={(e) => handleFilterChange('signedStatus', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">全部状态</option>
                <option value="completed">完全签名</option>
                <option value="partial">部分签名</option>
                <option value="none">未签名</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">金额范围</label>
              <input
                type="text"
                placeholder="例: 1000-2000"
                value={filters.amountRange}
                onChange={(e) => handleFilterChange('amountRange', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-6">
        <button
          onClick={handleCustomQuery}
          className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          查询统计
        </button>
      </div>
    </div>
  );
};

// 数据可视化图表组件（简化版）
const StatisticsChart = ({ data, loading }) => {
  const [distributionType, setDistributionType] = useState('violations'); // violations, overtime, duty, holidays

  if (loading || !data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h4 className="text-lg font-medium text-gray-900 mb-4">数据可视化</h4>
        <div className="flex justify-center items-center h-64 text-gray-500">
          {loading ? '数据加载中...' : '暂无数据'}
        </div>
      </div>
    );
  }

  // 角色分布数据
  const roleData = data.reduce((acc, item) => {
    const role = item.role;
    const roleName = role === 'admin' ? '管理员' : 
                    role === 'teacher' ? '教师' : 
                    role === 'caregiver' ? '后勤' : '用户（待分配）';
    
    const existing = acc.find(r => r.role === roleName);
    if (existing) {
      existing.count += 1;
      existing.totalViolations += Number(item.total_violations || 0);
      existing.totalOvertime += Number(item.total_overtime || 0);
      existing.totalDuty += Number(item.total_duty || 0);
      existing.totalHolidays += Number(item.total_public_holidays || 0);
    } else {
      acc.push({
        role: roleName,
        count: 1,
        totalViolations: Number(item.total_violations || 0),
        totalOvertime: Number(item.total_overtime || 0),
        totalDuty: Number(item.total_duty || 0),
        totalHolidays: Number(item.total_public_holidays || 0)
      });
    }
    return acc;
  }, []);

  const distributionOptions = [
    { value: 'violations', label: '迟到早退缺卡分布', unit: '次' },
    { value: 'overtime', label: '加班天数分布', unit: '天' },
    { value: 'duty', label: '值班天数分布', unit: '天' },
    { value: 'holidays', label: '公休假分布', unit: '天' }
  ];

  const getCurrentDistribution = () => {
    const option = distributionOptions.find(opt => opt.value === distributionType);
    const field = `total${distributionType.charAt(0).toUpperCase() + distributionType.slice(1)}`;
    const data = roleData.map(item => ({
      ...item,
      value: item[field] || 0
    }));
    return { option, data };
  };

  const { option: currentOption, data: distributionData } = getCurrentDistribution();

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <h4 className="text-lg font-medium text-gray-900 mb-4">👥 角色分布统计</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h5 className="font-medium text-gray-700 mb-3">人数分布</h5>
          <div className="space-y-2">
            {roleData.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div 
                    className={`w-4 h-4 rounded-full mr-3 ${
                      item.role === '管理员' ? 'bg-purple-500' :
                      item.role === '教师' ? 'bg-blue-500' :
                      item.role === '后勤' ? 'bg-green-500' : 'bg-gray-500'
                    }`}
                  ></div>
                  <span className="font-medium">{item.role}</span>
                </div>
                <div className="text-right">
                  <div className="font-bold text-lg">{item.count}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-3">
            <h5 className="font-medium text-gray-700">自定义分布</h5>
            <select
              value={distributionType}
              onChange={(e) => setDistributionType(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {distributionOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-3">
            {distributionData.map((item, index) => (
              <div key={index} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{item.role}</span>
                  <span>{item.value.toFixed(1)} {currentOption.unit}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      item.role === '管理员' ? 'bg-purple-500' :
                      item.role === '教师' ? 'bg-blue-500' :
                      item.role === '后勤' ? 'bg-green-500' : 'bg-gray-500'
                    }`}
                    style={{ 
                      width: `${Math.max((item.value / Math.max(...distributionData.map(r => r.value))) * 100, 3)}%` 
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomDataComparison = ({ token }) => {
  const [compareField, setCompareField] = useState('total_amount');
  const [periodA, setPeriodA] = useState({ year: new Date().getFullYear(), startMonth: 1, endMonth: 3 });
  const [periodB, setPeriodB] = useState({ year: new Date().getFullYear(), startMonth: 4, endMonth: 6 });
  const [comparisonData, setComparisonData] = useState(null);

  const compareFields = [
    { value: 'total_amount', label: '合计金额', unit: '元' },
    { value: 'personal_leave', label: '事假天数', unit: '天' },
    { value: 'sick_leave', label: '病假天数', unit: '天' },
    { value: 'public_holidays', label: '公休假天数', unit: '天' },
    { value: 'duty', label: '值班天数', unit: '天' },
    { value: 'overtime', label: '加班天数', unit: '天' },
    { value: 'substitute_duty', label: '顶班天数', unit: '天' },
    { value: 'hourly_leave', label: '小时假天数', unit: '天' },
    { value: 'fixed_holiday', label: '固定假天数', unit: '天' },
    { value: 'attendance_violation', label: '迟到早退缺卡', unit: '次' }
  ];

  const handleCompare = async () => {
    try {
      const response = await fetch(`${API_URL}/stats/comparison`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          field: compareField,
          periodA: periodA,
          periodB: periodB
        })
      });
      const data = await response.json();
      setComparisonData(data);
    } catch (error) {
      toast.error('对比数据获取失败');
    }
  };

  const selectedField = compareFields.find(f => f.value === compareField);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
      <h3 className="text-lg font-medium mb-4 flex items-center">
        <span className="mr-2">⚖️</span>
        自定义数据对比
      </h3>

      {/* 对比字段选择 */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">选择对比指标</label>
        <select
          value={compareField}
          onChange={(e) => setCompareField(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {compareFields.map(field => (
            <option key={field.value} value={field.value}>{field.label}</option>
          ))}
        </select>
      </div>

      {/* 时间段对比 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="p-4 border border-gray-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">对比期间 A</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">年份</label>
              <select
                value={periodA.year}
                onChange={(e) => setPeriodA({ ...periodA, year: parseInt(e.target.value) })}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => (
                  <option key={2024 + i} value={2024 + i}>{2024 + i}年</option>
                ))}
              </select>
            </div>
            <div className="flex space-x-2">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">起始月</label>
                <select
                  value={periodA.startMonth}
                  onChange={(e) => setPeriodA({ ...periodA, startMonth: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}月</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">结束月</label>
                <select
                  value={periodA.endMonth}
                  onChange={(e) => setPeriodA({ ...periodA, endMonth: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}月</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {comparisonData && (
            <div className="mt-4 p-3 bg-blue-50 rounded">
              <div className="text-lg font-bold text-blue-900">
                {selectedField?.label}: {comparisonData.periodA.value} {selectedField?.unit}
              </div>
              <div className="text-sm text-blue-700">
                平均: {comparisonData.periodA.average} {selectedField?.unit}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border border-gray-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">对比期间 B</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">年份</label>
              <select
                value={periodB.year}
                onChange={(e) => setPeriodB({ ...periodB, year: parseInt(e.target.value) })}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => (
                  <option key={2024 + i} value={2024 + i}>{2024 + i}年</option>
                ))}
              </select>
            </div>
            <div className="flex space-x-2">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">起始月</label>
                <select
                  value={periodB.startMonth}
                  onChange={(e) => setPeriodB({ ...periodB, startMonth: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}月</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">结束月</label>
                <select
                  value={periodB.endMonth}
                  onChange={(e) => setPeriodB({ ...periodB, endMonth: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}月</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {comparisonData && (
            <div className="mt-4 p-3 bg-green-50 rounded">
              <div className="text-lg font-bold text-green-900">
                {selectedField?.label}: {comparisonData.periodB.value} {selectedField?.unit}
              </div>
              <div className="text-sm text-green-700">
                平均: {comparisonData.periodB.average} {selectedField?.unit}
              </div>
              {comparisonData.difference && (
                <div className={`text-sm font-medium mt-2 ${
                  comparisonData.difference.type === 'increase' ? 'text-green-600' : 
                  comparisonData.difference.type === 'decrease' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  变化: {comparisonData.difference.text}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={handleCompare}
          className="px-6 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          开始对比分析
        </button>
      </div>
    </div>
  );
};

// 批量角色管理组件
const BatchRoleManager = ({ isOpen, onClose, onUpdate, token }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [targetRole, setTargetRole] = useState('user');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/users?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setUsers(data.data || []);
    } catch (error) {
      toast.error('获取用户列表失败');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleUserSelect = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleBatchUpdate = async () => {
    if (selectedUsers.length === 0) {
      toast.warn('请选择要修改的用户');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/users/batch-role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userIds: selectedUsers,
          role: targetRole
        })
      });

      if (response.ok) {
        toast.success(`成功修改 ${selectedUsers.length} 个用户的角色`);
        onUpdate();
        onClose();
      } else {
        throw new Error('批量修改失败');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'admin': return '管理员';
      case 'user': return '用户（待分配）';
      case 'teacher': return '教师';
      case 'caregiver': return '后勤';
      default: return role;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">批量修改用户角色</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="mr-2"
                  />
                  全选 ({selectedUsers.length}/{users.length})
                </label>
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">目标角色：</label>
                <select
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="user">用户（待分配）</option>
                  <option value="teacher">教师</option>
                  <option value="caregiver">后勤</option>
                  <option value="admin">管理员</option>
                </select>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">选择</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">用户名</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">当前角色</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className={selectedUsers.includes(user.id) ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={(e) => handleUserSelect(user.id, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 
                          user.role === 'teacher' ? 'bg-blue-100 text-blue-800' :
                          user.role === 'caregiver' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getRoleDisplayName(user.role)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleBatchUpdate}
                disabled={loading || selectedUsers.length === 0}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '修改中...' : `确认修改 (${selectedUsers.length})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 数据表格组件
const DataTable = ({ columns, data, loading = false, pagination, onPageChange }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                  暂无数据
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50">
                  {columns.map((column, colIndex) => (
                    <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {pagination && pagination.total > pagination.limit && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              上一页
            </button>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page === Math.ceil(pagination.total / pagination.limit)}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              下一页
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                显示第 <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> 到{' '}
                <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> 条，
                共 <span className="font-medium">{pagination.total}</span> 条记录
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">上一页</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {[...Array(Math.ceil(pagination.total / pagination.limit))].map((_, i) => {
                  const pageNum = i + 1;
                  const isCurrentPage = pageNum === pagination.page;
                  
                  if (
                    pageNum === 1 ||
                    pageNum === Math.ceil(pagination.total / pagination.limit) ||
                    (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          isCurrentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === pagination.page - 2 ||
                    pageNum === pagination.page + 2
                  ) {
                    return (
                      <span key={pageNum} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                
                <button
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page === Math.ceil(pagination.total / pagination.limit)}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">下一页</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 标签页组件
const TabNavigation = ({ tabs, activeTab, onTabChange }) => {
  return (
    <div className="border-b border-gray-200 bg-white rounded-t-lg">
      <nav className="-mb-px flex space-x-8 px-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
              activeTab === tab.key
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              {tab.icon && <span className="text-lg">{tab.icon}</span>}
              <span>{tab.label}</span>
              {tab.badge && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {tab.badge}
                </span>
              )}
            </div>
          </button>
        ))}
      </nav>
    </div>
  );
};

// 搜索框组件
const SearchBox = ({ placeholder, value, onChange, onSearch }) => {
  return (
    <div className="relative max-w-md">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input
        type="text"
        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && onSearch && onSearch()}
      />
    </div>
  );
};

// 模态框组件
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        
        <div className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle ${sizeClasses[size]} sm:w-full`}>
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">{title}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// 文件上传组件
const FileUploadSection = ({ onUploadAttendance }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setSelectedFileName(file.name);
    setUploading(true);
    
    try {
      await onUploadAttendance(file);
      toast.success(`文件 "${file.name}" 上传成功`);
      setSelectedFileName('');
      e.target.value = '';
    } catch (error) {
      toast.error(`上传失败: ${error.message || '服务器错误'}`);
      console.error('File upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-sm border">
      <div className="text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          stroke="currentColor"
          fill="none"
          viewBox="0 0 48 48"
        >
          <path
            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div className="mt-4">
          <label htmlFor="file-upload" className="cursor-pointer">
            <span className="mt-2 block text-sm font-medium text-gray-900">
              {uploading ? '正在上传...' : '选择考勤数据文件'}
            </span>
            <span className="mt-1 block text-sm text-gray-500">
              支持 .xlsx, .xls 格式，最大 10MB
            </span>
          </label>
          <input
            id="file-upload"
            name="file-upload"
            type="file"
            className="sr-only"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </div>
        
        {selectedFileName && (
          <div className="mt-4 text-sm text-gray-600">
            已选择: {selectedFileName}
          </div>
        )}
        
        {uploading && (
          <div className="mt-4">
            <div className="bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
            </div>
          </div>
        )}
        
        <div className="mt-6">
          <button
            onClick={() => document.getElementById('file-upload').click()}
            disabled={uploading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                上传中...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                选择文件上传
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Excel模板格式说明</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <div>• 表头字段：user_id, year, month, public_holidays, personal_leave, sick_leave, substitute_duty, hourly_leave, attendance_violation, duty, overtime, fixed_holiday, total_amount</div>
          <div>• user_id：可以是用户名（如"华鑫杰"）或用户ID数字</div>
          <div>• 数值字段：支持小数点格式（如2.5天）</div>
          <div>• 文件格式：Excel (.xlsx, .xls)，UTF-8编码</div>
        </div>
      </div>
    </div>
  );
};

// 添加用户模态框
const AddUserModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ username: '', password: '', role: 'user' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'admin': return '管理员';
      case 'user': return '用户（待分配）';
      case 'teacher': return '教师';
      case 'caregiver': return '后勤';
      default: return role;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="添加新用户">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">用户名</label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">密码</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">角色</label>
          <select
            name="role"
            value={formData.role}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="user">用户（待分配）</option>
            <option value="teacher">教师</option>
                              <option value="caregiver">后勤</option>
            <option value="admin">管理员</option>
          </select>
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            取消
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            添加用户
          </button>
        </div>
      </form>
    </Modal>
  );
};

// 修改密码模态框
const ChangePasswordModal = ({ isOpen, onClose, onSubmit, user }) => {
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(password);
    setPassword('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`修改密码 - ${user?.username}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">新密码</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            取消
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            确认修改
          </button>
        </div>
      </form>
    </Modal>
  );
};

// 签名确认模态框
const SignConfirmModal = ({ isOpen, onConfirm, onCancel, data }) => {
  const canvasRef = useRef(null);
  const [signaturePad, setSignaturePad] = useState(null);

  useEffect(() => {
    if (!isOpen || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);
    
    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)'
    });
    setSignaturePad(pad);

    return () => {
      if (pad) pad.off();
    };
  }, [isOpen]);

  const handleConfirm = () => {
    if (!signaturePad || signaturePad.isEmpty()) {
      toast.error('请先签名');
      return;
    }
    const signatureData = signaturePad.toDataURL();
    onConfirm(signatureData);
  };

  const clearSignature = () => {
    if (signaturePad) {
      signaturePad.clear();
    }
  };

  if (!data) return null;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="签名确认" size="lg">
      <div className="space-y-6">
        <p className="text-gray-600">请确认以下考勤数据并在下方签名：</p>
        
        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">公休假：</span>
              <span className="font-medium">{Number(data.public_holidays).toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">事假：</span>
              <span className="font-medium">{Number(data.personal_leave).toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">病假：</span>
              <span className="font-medium">{Number(data.sick_leave).toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">值班：</span>
              <span className="font-medium">{Number(data.duty).toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">加班：</span>
              <span className="font-medium">{Number(data.overtime).toFixed(1)}</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">顶班：</span>
              <span className="font-medium">{Number(data.substitute_duty).toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">小时假：</span>
              <span className="font-medium">{Number(data.hourly_leave).toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">固定假：</span>
              <span className="font-medium">{Number(data.fixed_holiday).toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">迟到早退缺卡：</span>
              <span className="font-medium">{Number(data.attendance_violation).toFixed(1)}</span>
            </div>
          </div>
          <div className="col-span-2 pt-2 border-t border-gray-200">
            <div className="flex justify-between text-lg font-bold">
              <span className="text-blue-600">合计金额：</span>
              <span className="text-blue-600">¥{Number(data.total_amount).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">电子签名：</span>
            <button
              type="button"
              onClick={clearSignature}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              清除签名
            </button>
          </div>
          <div className="border-2 border-gray-300 rounded-lg bg-white" style={{ height: '200px' }}>
            <canvas
              ref={canvasRef}
              className="w-full h-full rounded-lg"
              style={{ touchAction: 'none' }}
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            确认签名
          </button>
        </div>
      </div>
    </Modal>
  );
};

// 考勤详情模态框
const AttendanceDetailsModal = ({ isOpen, onClose, userId, year, month, token }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !userId) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/attendance/${userId}/${year}/${month}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();
        setDetails(data);
      } catch (error) {
        toast.error('获取详细数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [isOpen, userId, year, month, token]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`考勤详情 - ${details?.username || '加载中...'}`}>
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : details ? (
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: '公休假', value: details.public_holidays },
            { label: '事假', value: details.personal_leave },
            { label: '病假', value: details.sick_leave },
            { label: '顶班', value: details.substitute_duty },
            { label: '小时假', value: details.hourly_leave },
            { label: '迟到早退缺卡', value: details.attendance_violation },
            { label: '值班', value: details.duty },
            { label: '加班', value: details.overtime },
            { label: '固定假', value: details.fixed_holiday }
          ].map((item, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600">{item.label}</div>
              <div className="text-lg font-semibold">{Number(item.value).toFixed(1)}</div>
            </div>
          ))}
          <div className="col-span-2 p-3 bg-blue-50 rounded-lg">
            <div className="text-sm text-blue-600">合计金额</div>
            <div className="text-xl font-bold text-blue-600">¥{Number(details.total_amount).toFixed(2)}</div>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500">数据加载失败</div>
      )}
    </Modal>
  );
};

// 用户个人统计视图组件
const UserPersonalStatsView = ({ user, token }) => {
  const [timeRange, setTimeRange] = useState({
    startYear: new Date().getFullYear(),
    startMonth: 1,
    endYear: new Date().getFullYear(),
    endMonth: new Date().getMonth() + 1
  });
  const [personalStats, setPersonalStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPersonalStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startYear: timeRange.startYear,
        startMonth: timeRange.startMonth,
        endYear: timeRange.endYear,
        endMonth: timeRange.endMonth
      });

      const response = await fetch(`${API_URL}/stats/custom?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      // 用户只能看到自己的数据，所以应该只有一条记录
      setPersonalStats(data.data?.[0] || null);
    } catch (error) {
      toast.error('获取个人统计失败');
    } finally {
      setLoading(false);
    }
  }, [token, timeRange]);

  useEffect(() => {
    fetchPersonalStats();
  }, [fetchPersonalStats]);

  return (
    <div className="space-y-6">
      {/* 时间范围选择器（简化版） */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium mb-4 flex items-center">
          <span className="mr-2">📊</span>
          我的考勤统计
        </h3>
        
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">统计时间：</label>
            <select
              value={timeRange.startYear}
              onChange={(e) => setTimeRange({ ...timeRange, startYear: parseInt(e.target.value) })}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => (
                <option key={2024 + i} value={2024 + i}>{2024 + i}年</option>
              ))}
            </select>
            <select
              value={timeRange.startMonth}
              onChange={(e) => setTimeRange({ ...timeRange, startMonth: parseInt(e.target.value) })}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}月</option>
              ))}
            </select>
            <span className="text-gray-500">至</span>
            <select
              value={timeRange.endYear}
              onChange={(e) => setTimeRange({ ...timeRange, endYear: parseInt(e.target.value) })}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => (
                <option key={2024 + i} value={2024 + i}>{2024 + i}年</option>
              ))}
            </select>
            <select
              value={timeRange.endMonth}
              onChange={(e) => setTimeRange({ ...timeRange, endMonth: parseInt(e.target.value) })}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}月</option>
              ))}
            </select>
          </div>

          <button
            onClick={fetchPersonalStats}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          >
            查询统计
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : personalStats ? (
        <div className="space-y-6">
          {/* 基础统计卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="查询月数"
              value={personalStats.query_months}
              color="blue"
              icon="📅"
            />
            <StatCard
              title="有数据月数"
              value={personalStats.data_months}
              color="green"
              icon="📊"
            />
            <StatCard
              title="已签名月数"
              value={personalStats.signed_months}
              color="purple"
              icon="✅"
            />
            <StatCard
              title="签名完成率"
              value={`${personalStats.completion_rate}%`}
              color="yellow"
              icon="📈"
            />
          </div>

          {/* 详细统计 */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-medium text-gray-900 mb-4">详细统计数据</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: '公休假', value: personalStats.total_public_holidays, unit: '天', color: 'blue' },
                { label: '事假', value: personalStats.total_personal_leave, unit: '天', color: 'yellow' },
                { label: '病假', value: personalStats.total_sick_leave, unit: '天', color: 'red' },
                { label: '固定假', value: personalStats.total_fixed_holiday, unit: '天', color: 'green' },
                { label: '小时假', value: personalStats.total_hourly_leave, unit: '天', color: 'purple' },
                { label: '迟到早退缺卡', value: personalStats.total_violations, unit: '次', color: 'red' },
                { label: '值班', value: personalStats.total_duty, unit: '天', color: 'blue' },
                { label: '加班', value: personalStats.total_overtime, unit: '天', color: 'yellow' },
                { label: '顶班', value: personalStats.total_substitute_duty, unit: '天', color: 'green' }
              ].map((item, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">{item.label}</div>
                  <div className="text-xl font-bold text-gray-900">{Number(item.value || 0).toFixed(1)} {item.unit}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 金额统计 */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h4 className="text-lg font-medium text-gray-900 mb-4">金额统计</h4>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <StatCard
                title="总金额"
                value={`¥${Number(personalStats.total_amount || 0).toFixed(2)}`}
                subtitle={`平均月金额：¥${(Number(personalStats.total_amount || 0) / Math.max(personalStats.data_months || 1, 1)).toFixed(2)}`}
                color="green"
                icon="💰"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
          <div className="text-gray-500">该时间段内暂无考勤数据</div>
        </div>
      )}
    </div>
  );
};

// 自定义统计视图
const CustomStatisticsView = ({ token }) => {
  const [timeRange, setTimeRange] = useState({
    startYear: new Date().getFullYear(),
    startMonth: 1,
    endYear: new Date().getFullYear(),
    endMonth: new Date().getMonth() + 1
  });
  
  const [statisticsData, setStatisticsData] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'admin': return '管理员';
      case 'teacher': return '教师';
      case 'caregiver': return '后勤';
      default: return role;
    }
  };

  // 完整的统计字段配置（添加迟到早退缺卡）
  const statisticsColumns = [
    { key: 'username', title: '用户名', width: '120px', fixed: true },
    { key: 'role', title: '角色', width: '80px', render: (value) => (
      <span className={`px-2 py-1 text-xs rounded-full ${
        value === 'admin' ? 'bg-purple-100 text-purple-800' : 
        value === 'teacher' ? 'bg-blue-100 text-blue-800' :
        value === 'caregiver' ? 'bg-green-100 text-green-800' :
        'bg-gray-100 text-gray-800'
      }`}>
        {getRoleDisplayName(value)}
      </span>
    )},
    { key: 'query_months', title: '查询月数', width: '80px' },
    { key: 'data_months', title: '有数据月数', width: '100px' },
    { key: 'signed_months', title: '已签名月数', width: '100px' },
    { key: 'completion_rate', title: '签名完成率', width: '100px', render: (value) => `${value}%` },
    { key: 'total_public_holidays', title: '公休假(天)', width: '100px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_personal_leave', title: '事假(天)', width: '100px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_sick_leave', title: '病假(天)', width: '100px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_fixed_holiday', title: '固定假(天)', width: '100px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_hourly_leave', title: '小时假(天)', width: '100px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_violations', title: '迟到早退缺卡(次)', width: '120px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_duty', title: '值班(天)', width: '100px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_overtime', title: '加班(天)', width: '100px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_substitute_duty', title: '顶班(天)', width: '100px', render: (value) => Number(value || 0).toFixed(1) },
    { key: 'total_amount', title: '总金额(元)', width: '120px', render: (value) => `¥${Number(value || 0).toFixed(2)}` }
  ];

  const fetchStatisticsData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startYear: timeRange.startYear,
        startMonth: timeRange.startMonth,
        endYear: timeRange.endYear,
        endMonth: timeRange.endMonth
      });

      // 添加筛选条件到URL参数
      if (filters.role) {
        params.append('role', filters.role);
      }
      if (filters.signedStatus) {
        params.append('signedStatus', filters.signedStatus);
      }
      if (filters.amountRange) {
        params.append('amountRange', filters.amountRange);
      }

      const response = await fetch(`${API_URL}/stats/custom?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStatisticsData(data.data || []);
    } catch (error) {
      toast.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  }, [token, timeRange, filters]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        startYear: timeRange.startYear,
        startMonth: timeRange.startMonth,
        endYear: timeRange.endYear,
        endMonth: timeRange.endMonth,
        ...filters
      });

      const response = await fetch(`${API_URL}/export/custom-statistics?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('导出失败');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `自定义统计_${timeRange.startYear}${timeRange.startMonth}-${timeRange.endYear}${timeRange.endMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('导出成功');
    } catch (error) {
      toast.error('导出失败: ' + error.message);
    }
  };

  useEffect(() => {
    fetchStatisticsData();
  }, [fetchStatisticsData]);

  return (
    <div className="space-y-6">
      {/* 时间范围选择器（带筛选功能） */}
      <TimeRangeSelector 
        onRangeChange={setTimeRange}
        currentRange={timeRange}
        onFilterChange={setFilters}
        currentFilter={filters}
      />

      {/* 数据可视化图表 */}
      <StatisticsChart data={statisticsData} loading={loading} />

      {/* 自定义数据对比 */}
      <CustomDataComparison token={token} />

      {/* 操作按钮区域 */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900">
            统计结果 ({statisticsData.length} 条记录)
          </h3>
          {loading && (
            <div className="flex items-center text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              加载中...
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700"
          >
            <span className="mr-2">📤</span>
            导出数据
          </button>
          <button
            onClick={fetchStatisticsData}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          >
            <span className="mr-2">🔄</span>
            刷新数据
          </button>
        </div>
      </div>

      {/* 统计数据表格 */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h4 className="text-lg font-medium text-gray-900">📋 详细数据表格</h4>
          {Object.values(filters).some(v => v) && (
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="text-sm text-gray-600">当前筛选：</span>
              {filters.role && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  角色: {getRoleDisplayName(filters.role)}
                </span>
              )}
              {filters.signedStatus && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  签名状态: {filters.signedStatus === 'completed' ? '完全签名' : filters.signedStatus === 'partial' ? '部分签名' : '未签名'}
                </span>
              )}
              {filters.amountRange && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  金额范围: {filters.amountRange}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {statisticsColumns.map((column, index) => (
                  <th
                    key={index}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    style={{ width: column.width }}
                  >
                    {column.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {statisticsData.length === 0 ? (
                <tr>
                  <td colSpan={statisticsColumns.length} className="px-6 py-12 text-center text-gray-500">
                    {loading ? '数据加载中...' : '暂无符合条件的数据'}
                  </td>
                </tr>
              ) : (
                statisticsData.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-50">
                    {statisticsColumns.map((column, colIndex) => (
                      <td key={colIndex} className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {column.render ? column.render(row[column.key], row) : row[column.key]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// 用户视图组件
const UserView = ({ attendanceData, onSignAttendance, error, selectedYear, selectedMonth, onYearChange, onMonthChange, user, token }) => {
  const [activeTab, setActiveTab] = useState('current');
  const currentRecord = attendanceData[0] || {};

  const tabs = [
    { key: 'current', label: '当月考勤', icon: '📝' },
    { key: 'statistics', label: '我的统计', icon: '📊' }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}
      
      {/* 标签页导航 */}
      <div className="bg-white rounded-lg shadow-sm border">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        
        <div className="p-6">
          {/* 当月考勤页面 */}
          {activeTab === 'current' && (
            <div className="space-y-6">
              {/* 时间选择器 */}
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">查看时间：</label>
                <select
                  value={selectedYear}
                  onChange={(e) => onYearChange(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => (
                    <option key={2024 + i} value={2024 + i}>{2024 + i}年</option>
                  ))}
                </select>
                <select
                  value={selectedMonth}
                  onChange={(e) => onMonthChange(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}月
                    </option>
                  ))}
                </select>
              </div>

              <h2 className="text-2xl font-bold text-gray-900">我的考勤详情</h2>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: '公休假', value: currentRecord.public_holidays, color: 'blue', icon: '🏖️' },
                  { label: '事假', value: currentRecord.personal_leave, color: 'yellow', icon: '📝' },
                  { label: '病假', value: currentRecord.sick_leave, color: 'red', icon: '🏥' },
                  { label: '顶班', value: currentRecord.substitute_duty, color: 'green', icon: '🔄' },
                  { label: '小时假', value: currentRecord.hourly_leave, color: 'purple', icon: '⏰' },
                  { label: '迟到早退缺卡', value: currentRecord.attendance_violation, color: 'red', icon: '⚠️' },
                  { label: '值班', value: currentRecord.duty, color: 'blue', icon: '🛡️' },
                  { label: '加班', value: currentRecord.overtime, color: 'yellow', icon: '💼' },
                  { label: '固定假', value: currentRecord.fixed_holiday, color: 'green', icon: '📅' }
                ].map((item, index) => (
                  <StatCard
                    key={index}
                    title={item.label}
                    value={Number(item.value || 0).toFixed(1)}
                    color={item.color}
                    icon={item.icon}
                  />
                ))}
                
                <div className="md:col-span-3 lg:col-span-4">
                  <StatCard
                    title="合计金额"
                    value={`¥${Number(currentRecord.total_amount || 0).toFixed(2)}`}
                    color="blue"
                    icon="💰"
                  />
                </div>
              </div>

              {currentRecord.data_status === 'uploaded' && !currentRecord.signed && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>待签名确认：</strong>请确认以上考勤信息并进行电子签名
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={onSignAttendance}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      立即签名确认
                    </button>
                  </div>
                </div>
              )}

              {currentRecord.signed && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700">
                        <strong>已完成签名确认</strong> - 签名时间：{new Date(currentRecord.sign_date).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {currentRecord.data_status === 'pending' && (
                <div className="bg-gray-50 border-l-4 border-gray-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-700">
                        该月份考勤数据暂未上传，请等待管理员处理
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 个人统计页面 */}
          {activeTab === 'statistics' && (
            <UserPersonalStatsView user={user} token={token} />
          )}
        </div>
      </div>
    </div>
  );
};

// 管理员视图组件
const AdminView = ({ monthlyStats, onUploadAttendance, selectedYear, selectedMonth, onYearChange, onMonthChange, token }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  // 各种状态管理
  const [userList, setUserList] = useState({ data: [], total: 0 });
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [showBatchRole, setShowBatchRole] = useState(false);

  const [signedUsers, setSignedUsers] = useState({ data: [], total: 0 });
  const [signedSearch, setSignedSearch] = useState('');
  const [signedPage, setSignedPage] = useState(1);

  const [unsignedUsers, setUnsignedUsers] = useState({ data: [], total: 0 });
  const [unsignedPage, setUnsignedPage] = useState(1);

  const [annualStats, setAnnualStats] = useState({ data: [], total: 0 });
  const [annualSearch, setAnnualSearch] = useState('');
  const [annualPage, setAnnualPage] = useState(1);

  // 模态框状态
  const [showAddUser, setShowAddUser] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
  const [attendanceDetailsParams, setAttendanceDetailsParams] = useState(null);

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'admin': return '管理员';
      case 'teacher': return '教师';
      case 'caregiver': return '后勤';
      default: return role;
    }
  };

  // 数据获取函数
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/users?page=${userPage}&limit=10&search=${userSearch}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setUserList(data);
    } catch (error) {
      toast.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  }, [token, userPage, userSearch]);

  const fetchSignedUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/stats/monthly/${selectedYear}/${selectedMonth}/signed?page=${signedPage}&search=${signedSearch}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setSignedUsers(data);
    } catch (error) {
      toast.error('获取已签名用户失败');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth, signedPage, signedSearch]);

  const fetchUnsignedUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/stats/monthly/${selectedYear}/${selectedMonth}/unsigned?page=${unsignedPage}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setUnsignedUsers(data);
    } catch (error) {
      toast.error('获取未签名用户失败');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth, unsignedPage]);

  const fetchAnnualStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/stats/annual/${selectedYear}/search?page=${annualPage}&username=${annualSearch}&limit=10`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setAnnualStats(data);
    } catch (error) {
      toast.error('获取年度统计失败');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, annualPage, annualSearch]);

  // 用户管理操作
  const handleAddUser = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
      if (response.ok) {
        toast.success('用户添加成功');
        setShowAddUser(false);
        fetchUsers();
      } else {
        const data = await response.json();
        throw new Error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangePassword = async (password) => {
    try {
      const response = await fetch(`${API_URL}/users/${selectedUser.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ password })
      });
      if (response.ok) {
        toast.success('密码修改成功');
        setShowChangePassword(false);
        setSelectedUser(null);
      } else {
        const data = await response.json();
        throw new Error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('确定要删除该用户吗？此操作将同时删除用户的所有考勤记录，且不可恢复。')) {
      try {
        const response = await fetch(`${API_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          toast.success('用户删除成功');
          fetchUsers();
        } else {
          throw new Error('删除用户失败');
        }
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(
        `${API_URL}/export/${selectedYear}/${selectedMonth}`,
        { 
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!response.ok) {
        throw new Error('导出失败');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `考勤数据_${selectedYear}年${selectedMonth}月.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('导出成功');
    } catch (error) {
      toast.error('导出失败');
    }
  };

  const showAttendanceDetail = (userId, year, month) => {
    setAttendanceDetailsParams({ userId, year, month });
    setShowAttendanceDetails(true);
  };

  // Effect hooks
  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [activeTab, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'signed') fetchSignedUsers();
  }, [activeTab, fetchSignedUsers]);

  useEffect(() => {
    if (activeTab === 'unsigned') fetchUnsignedUsers();
  }, [activeTab, fetchUnsignedUsers]);

  useEffect(() => {
    if (activeTab === 'annual') fetchAnnualStats();
  }, [activeTab, fetchAnnualStats]);

  // 标签页配置
  const tabs = [
    {
      key: 'overview',
      label: '考勤统计',
      icon: '📊',
      badge: monthlyStats?.total_users
    },
    {
      key: 'custom',
      label: '自定义统计',
      icon: '📈'
    },
    {
      key: 'users',
      label: '用户管理',
      icon: '👥',
      badge: userList.total
    },
    {
      key: 'upload',
      label: '导入数据',
      icon: '📤'
    },
    {
      key: 'signed',
      label: '已签名',
      icon: '✅',
      badge: monthlyStats?.signed_users
    },
    {
      key: 'unsigned',
      label: '未签名',
      icon: '⏳',
      badge: monthlyStats?.unsigned_users
    },
    {
      key: 'annual',
      label: '年度统计',
      icon: '📈'
    }
  ];

  // 表格列配置
  const userColumns = [
    { key: 'id', title: 'ID' },
    { key: 'username', title: '用户名' },
    { 
      key: 'role', 
      title: '角色',
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'admin' ? 'bg-purple-100 text-purple-800' : 
          value === 'teacher' ? 'bg-blue-100 text-blue-800' :
          value === 'caregiver' ? 'bg-green-100 text-green-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {getRoleDisplayName(value)}
        </span>
      )
    },
    {
      key: 'actions',
      title: '操作',
      render: (_, user) => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setSelectedUser(user);
              setShowChangePassword(true);
            }}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            修改密码
          </button>
          <button
            onClick={() => handleDeleteUser(user.id)}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            删除
          </button>
        </div>
      )
    }
  ];

  const signedColumns = [
    { key: 'username', title: '用户名' },
    { 
      key: 'total_amount', 
      title: '金额',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`
    },
    { 
      key: 'attendance_violation', 
      title: '迟到早退缺卡',
      render: (value) => `${Number(value || 0).toFixed(1)}次`
    },
    { 
      key: 'sign_date', 
      title: '签名时间',
      render: (value) => new Date(value).toLocaleString()
    },
    {
      key: 'signature_data',
      title: '签名',
      render: (value) => value ? (
        <img src={value} alt="签名" className="h-12 w-auto border rounded" />
      ) : '无签名'
    },
    {
      key: 'actions',
      title: '操作',
      render: (_, user) => (
        <button
          onClick={() => showAttendanceDetail(user.id, selectedYear, selectedMonth)}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          查看详情
        </button>
      )
    }
  ];

  const unsignedColumns = [
    { key: 'username', title: '用户名' },
    { 
      key: 'total_amount', 
      title: '金额',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`
    },
    { 
      key: 'data_status', 
      title: '状态',
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'uploaded' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value === 'uploaded' ? '待签名' : '无数据'}
        </span>
      )
    },
    {
      key: 'actions',
      title: '操作',
      render: (_, user) => (
        <button
          onClick={() => showAttendanceDetail(user.id, selectedYear, selectedMonth)}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          查看详情
        </button>
      )
    }
  ];

  const annualColumns = [
    { key: 'username', title: '用户名' },
    { key: 'months_with_data', title: '有数据月份' },
    { key: 'months_signed', title: '已签名月份' },
    { 
      key: 'total_duty_days', 
      title: '值班天数',
      render: (value) => Number(value || 0).toFixed(1)
    },
    { 
      key: 'total_overtime', 
      title: '加班天数',
      render: (value) => Number(value || 0).toFixed(1)
    },
    { 
      key: 'total_holidays', 
      title: '公休假',
      render: (value) => Number(value || 0).toFixed(1)
    },
    { 
      key: 'total_year_amount', 
      title: '年度总额',
      render: (value) => `¥${Number(value || 0).toFixed(2)}`
    }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        
        <div className="p-6">
          {/* 考勤统计页面 */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* 月份选择器 */}
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">查看时间：</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => onYearChange(parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => (
                      <option key={2024 + i} value={2024 + i}>{2024 + i}年</option>
                    ))}
                  </select>
                  <select
                    value={selectedMonth}
                    onChange={(e) => onMonthChange(parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}月
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedYear}年{selectedMonth}月考勤统计概览
                </h3>
                <button
                  onClick={handleExport}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  导出Excel
                </button>
              </div>
              
              {monthlyStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard
                    title="总用户数"
                    value={monthlyStats.total_users || 0}
                    color="blue"
                    icon="👥"
                  />
                  <StatCard
                    title="有数据用户"
                    value={monthlyStats.users_with_data || 0}
                    subtitle={`${((monthlyStats.users_with_data / monthlyStats.total_users) * 100).toFixed(1)}%`}
                    color="purple"
                    icon="📊"
                  />
                  <StatCard
                    title="已签名"
                    value={monthlyStats.signed_users || 0}
                    subtitle={`${((monthlyStats.signed_users / (monthlyStats.users_with_data || 1)) * 100).toFixed(1)}%`}
                    color="green"
                    icon="✅"
                  />
                  <StatCard
                    title="待签名"
                    value={monthlyStats.unsigned_users || 0}
                    subtitle={`${((monthlyStats.unsigned_users / (monthlyStats.users_with_data || 1)) * 100).toFixed(1)}%`}
                    color="yellow"
                    icon="⏳"
                  />
                </div>
              )}
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-md font-medium text-gray-900 mb-4">快速操作</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    导入考勤数据
                  </button>
                  <button
                    onClick={() => setActiveTab('unsigned')}
                    className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-5 w-5 mr-2 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    查看待签名用户
                  </button>
                  <button
                    onClick={() => setActiveTab('signed')}
                    className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    查看已签名用户
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 自定义统计页面 */}
          {activeTab === 'custom' && (
            <CustomStatisticsView token={token} />
          )}

          {/* 用户管理页面 */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">用户管理</h3>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowBatchRole(true)}
                    className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700"
                  >
                    <span className="mr-2">👥</span>
                    批量管理角色
                  </button>
                  <button
                    onClick={() => setShowAddUser(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    添加用户
                  </button>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <SearchBox
                  placeholder="搜索用户名..."
                  value={userSearch}
                  onChange={setUserSearch}
                  onSearch={fetchUsers}
                />
              </div>
              
              <DataTable
                columns={userColumns}
                data={userList.data}
                loading={loading}
                pagination={{
                  page: userPage,
                  limit: 10,
                  total: userList.total
                }}
                onPageChange={setUserPage}
              />
            </div>
          )}

          {/* 导入数据页面 */}
          {activeTab === 'upload' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">导入考勤数据</h3>
                <p className="text-sm text-gray-600">
                  支持导入Excel格式的考勤数据文件。请确保文件格式正确，包含必要的字段信息。
                </p>
              </div>
              
              <FileUploadSection onUploadAttendance={onUploadAttendance} />
            </div>
          )}

          {/* 已签名用户页面 */}
          {activeTab === 'signed' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  已签名用户 ({selectedYear}年{selectedMonth}月)
                </h3>
              </div>
              
              <div className="flex justify-between items-center">
                <SearchBox
                  placeholder="搜索用户名..."
                  value={signedSearch}
                  onChange={setSignedSearch}
                  onSearch={fetchSignedUsers}
                />
              </div>
              
              <DataTable
                columns={signedColumns}
                data={signedUsers.data}
                loading={loading}
                pagination={{
                  page: signedPage,
                  limit: 10,
                  total: signedUsers.total
                }}
                onPageChange={setSignedPage}
              />
            </div>
          )}

          {/* 未签名用户页面 */}
          {activeTab === 'unsigned' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  未签名用户 ({selectedYear}年{selectedMonth}月)
                </h3>
              </div>
              
              <DataTable
                columns={unsignedColumns}
                data={unsignedUsers.data}
                loading={loading}
                pagination={{
                  page: unsignedPage,
                  limit: 10,
                  total: unsignedUsers.total
                }}
                onPageChange={setUnsignedPage}
              />
            </div>
          )}

          {/* 年度统计页面 */}
          {activeTab === 'annual' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedYear}年度统计
                </h3>
              </div>
              
              <div className="flex justify-between items-center">
                <SearchBox
                  placeholder="搜索用户名..."
                  value={annualSearch}
                  onChange={setAnnualSearch}
                  onSearch={fetchAnnualStats}
                />
              </div>
              
              <DataTable
                columns={annualColumns}
                data={annualStats.data}
                loading={loading}
                pagination={{
                  page: annualPage,
                  limit: 10,
                  total: annualStats.total
                }}
                onPageChange={setAnnualPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* 模态框 */}
      <AddUserModal
        isOpen={showAddUser}
        onClose={() => setShowAddUser(false)}
        onSubmit={handleAddUser}
      />
      
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSubmit={handleChangePassword}
        user={selectedUser}
      />
      
      <AttendanceDetailsModal
        isOpen={showAttendanceDetails}
        onClose={() => setShowAttendanceDetails(false)}
        userId={attendanceDetailsParams?.userId}
        year={attendanceDetailsParams?.year}
        month={attendanceDetailsParams?.month}
        token={token}
      />

      {/* 批量角色管理模态框 */}
      <BatchRoleManager
        isOpen={showBatchRole}
        onClose={() => setShowBatchRole(false)}
        onUpdate={() => {
          fetchUsers();
          // 如果在用户管理页面，刷新用户列表
          if (activeTab === 'users') {
            fetchUsers();
          }
        }}
        token={token}
      />
    </div>
  );
};

// 主应用组件
const AttendanceSystem = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || 'null'));
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [error, setError] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        setToken(data.token);
        setUser(data.user);
        setIsLoggedIn(true);
        toast.success('登录成功');
      } else {
        throw new Error(data.message || '登录失败');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
    setAttendanceData([]);
    setMonthlyStats(null);
    setError(null);
    toast.info('已退出登录');
  };

  const fetchAttendance = useCallback(async () => {
    if (!user?.id || !token) return;
    try {
      const response = await fetch(
        `${API_URL}/attendance/${user.id}/${selectedYear}/${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setAttendanceData([data]);
    } catch (err) {
      toast.error('获取考勤数据失败');
      setError('获取考勤数据失败');
    }
  }, [user, token, selectedYear, selectedMonth]);

  const fetchMonthlyStats = useCallback(async () => {
    if (user?.role !== 'admin' || !token) return;
    try {
      const response = await fetch(
        `${API_URL}/stats/monthly/${selectedYear}/${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setMonthlyStats(data);
    } catch (err) {
      toast.error('获取月度统计失败');
    }
  }, [user, token, selectedYear, selectedMonth]);

  const uploadAttendance = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/upload-attendance`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('服务器响应格式错误');
      }
      
      if (response.ok) {
        fetchAttendance();
        fetchMonthlyStats();
        return true;
      } else {
        throw new Error(data.error || data.message || '上传失败');
      }
    } catch (err) {
      throw new Error(err.message);
    }
  };

  const signAttendance = async (signatureData) => {
    try {
      const response = await fetch(`${API_URL}/attendance/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.id,
          year: selectedYear,
          month: selectedMonth,
          signatureData
        })
      });
      
      if (response.ok) {
        toast.success('签名确认成功');
        setShowSignModal(false);
        fetchAttendance();
        fetchMonthlyStats();
      } else {
        const data = await response.json();
        throw new Error(data.message);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'admin': return '管理员';
      case 'teacher': return '教师';
      case 'caregiver': return '后勤';
      default: return role;
    }
  };

  useEffect(() => {
    if (isLoggedIn && token && user) {
      fetchAttendance();
      if (user.role === 'admin') {
        fetchMonthlyStats();
      }
    }
  }, [isLoggedIn, token, user, fetchAttendance, fetchMonthlyStats]);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
        {/* 背景装饰元素 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-pulse animation-delay-4000"></div>
        </div>

        <div className="bg-white p-8 rounded-3xl shadow-2xl w-96 border border-gray-100 relative z-10 backdrop-blur-sm bg-opacity-95">
          <div className="text-center mb-8">
            {/* 系统图标 */}
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              江溪幼儿园
            </h1>
            <h2 className="text-xl text-gray-600 mb-3">考勤管理系统</h2>
            
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="px-3 py-1 text-xs rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
                2.0
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border border-blue-200">
                智能考勤
              </span>
            </div>
            
            {/* 特性标签 */}
            <div className="flex flex-wrap justify-center gap-2 text-xs">
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full">📊 数据统计</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full">✍️ 电子签名</span>
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full">📱 移动友好</span>
            </div>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}
          
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            login(formData.get('username'), formData.get('password'));
          }}>
            <div className="space-y-5">
              <div className="group">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  用户名
                </label>
                <input
                  type="text"
                  name="username"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 group-hover:border-gray-400"
                  placeholder="请输入用户名"
                  required
                />
              </div>
              <div className="group">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  密码
                </label>
                <input
                  type="password"
                  name="password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 group-hover:border-gray-400"
                  placeholder="请输入密码"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                登录系统
              </button>
            </div>
          </form>
          
          {/* 底部信息 */}
          <div className="mt-6 text-center">
            <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
              <span className="flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                安全登录
              </span>
              <span className="flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" clipRule="evenodd" />
                </svg>
                数据保护
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              © 2024-2026 江溪幼儿园 · 智能考勤管理系统
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" />
      
      {/* 顶部导航栏 */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-gray-900">江溪幼儿园考勤管理系统</h1>
              <span className="px-3 py-1 text-xs rounded-full bg-green-100 text-green-800">
                2.0
              </span>
              <span className="px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                {getRoleDisplayName(user?.role)}
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">用户：</span>
                <span className="text-sm font-medium text-gray-900">{user?.username}</span>
              </div>
              <button
                onClick={logout}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                退出登录
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 根据用户角色显示不同的视图 */}
        {user?.role === 'admin' ? (
          <AdminView
            monthlyStats={monthlyStats}
            onUploadAttendance={uploadAttendance}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            onYearChange={setSelectedYear}
            onMonthChange={setSelectedMonth}
            token={token}
          />
        ) : (
          <UserView
            attendanceData={attendanceData}
            onSignAttendance={() => setShowSignModal(true)}
            error={error}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            onYearChange={setSelectedYear}
            onMonthChange={setSelectedMonth}
            user={user}
            token={token}
          />
        )}
      </main>

      {/* 签名确认模态框 */}
      <SignConfirmModal
        isOpen={showSignModal}
        onConfirm={signAttendance}
        onCancel={() => setShowSignModal(false)}
        data={attendanceData[0]}
      />
    </div>
  );
};

export default AttendanceSystem;